import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Save, Trash2, MousePointerClick, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CATEGORIES = ["Creative without a studio", "Selling where people actually buy", "Money in and money out", "Moving the goods", "Building software without developers", "The work that runs itself"];

const EMPTY = { name: "", category: "Moving the goods", what_it_does: "", honest_take: "", replaced: "", url: "", is_affiliate: false, logo: "", sort_order: 99, active: true };

const isDraftVal = (v) => !(v || "").trim() || v.trim().toUpperCase().startsWith("DRAFT");

function ToolForm({ initial, onSave, saving, testid }) {
  const [f, setF] = useState(initial);
  const takeMissing = isDraftVal(f.honest_take);
  const replacedMissing = isDraftVal(f.replaced);
  const gateBlocked = takeMissing || replacedMissing;
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`${testid}-name`} /></div>
        <div><Label className="text-xs">Category</Label>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select></div>
        <div><Label className="text-xs">URL</Label><Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" data-testid={`${testid}-url`} /></div>
        <div><Label className="text-xs">Sort order</Label><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></div>
      </div>
      <div><Label className="text-xs">What it replaced (the before — shows struck-through above the tool name)</Label><Input value={f.replaced} onChange={(e) => setF({ ...f, replaced: e.target.value })} placeholder='e.g. "A ₹40,000 product shoot, twice a year."' data-testid={`${testid}-replaced`} /></div>
      <div><Label className="text-xs">What it does (one line)</Label><Input value={f.what_it_does} onChange={(e) => setF({ ...f, what_it_does: e.target.value })} data-testid={`${testid}-what`} /></div>
      <div>
        <Label className="text-xs">Honest take (2–3 sentences — must name something the tool is bad at)</Label>
        <textarea value={f.honest_take} onChange={(e) => setF({ ...f, honest_take: e.target.value })} rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`${testid}-take`} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label className="text-xs">Logo URL (optional)</Label><Input value={f.logo} onChange={(e) => setF({ ...f, logo: e.target.value })} /></div>
        <div className="flex items-end gap-5 pb-1">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.is_affiliate} onChange={(e) => setF({ ...f, is_affiliate: e.target.checked })} data-testid={`${testid}-affiliate`} /> Affiliate link</label>
          <label className={`flex items-center gap-2 text-sm ${gateBlocked ? "cursor-not-allowed text-slate-400" : "text-slate-700"}`} title={gateBlocked ? "Fill the replaced line and write a real honest take first" : ""}>
            <input type="checkbox" checked={f.active} disabled={gateBlocked}
              onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid={`${testid}-active`} /> Active
          </label>
        </div>
      </div>
      {gateBlocked && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700" data-testid={`${testid}-gate`}>
          Not publishable yet: {takeMissing ? "honest take missing (name something it's bad at)" : ""}{takeMissing && replacedMissing ? " · " : ""}{replacedMissing ? "replaced line missing" : ""}. The tool stays hidden publicly until both are real.
        </p>
      )}
      <Button onClick={() => onSave({ ...f, sort_order: parseInt(f.sort_order) || 99, active: gateBlocked ? false : f.active })} disabled={saving || !f.name} size="sm" className="rounded-md bg-navy text-white" data-testid={`${testid}-save`}>
        <Save className="mr-1.5 h-3.5 w-3.5" /> {saving ? "Saving…" : "Save tool"}
      </Button>
    </div>
  );
}

export function StackAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["stack-admin"] });
  const { data } = useQuery({ queryKey: ["stack-admin"], queryFn: async () => (await api.get("/stack/admin/tools")).data });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (id, payload) => {
    setSaving(true);
    try {
      if (id) await api.put(`/stack/admin/tools/${id}`, payload);
      else await api.post("/stack/admin/tools", payload);
      toast.success("Saved"); setAdding(false); setEditing(null); invalidate();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="stack-admin">
      <div>
        <h3 className="font-heading text-xl font-bold text-navy">The Stack</h3>
        <p className="mt-1 max-w-2xl rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800" data-testid="stack-admin-rule">
          1. A tool goes on this page only if Shubham actually uses it, and comes off when he stops.<br />
          2. Every entry needs a replaced line and an honest take that names something the tool is bad at. No entry publishes without both.
        </p>
      </div>

      <div className="space-y-3">
        {(data || []).map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`stack-admin-tool-${t.id}`}>
            {editing === t.id ? (
              <ToolForm initial={t} saving={saving} testid={`stack-edit-${t.id}`} onSave={(p) => save(t.id, p)} />
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-navy">{t.name}</p>
                    {t.is_affiliate && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">affiliate</span>}
                    {t.draft && <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700" data-testid={`stack-draft-${t.id}`}><AlertTriangle className="h-3 w-3" /> DRAFT — replace before publishing</span>}
                    {!t.active && <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">inactive</span>}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{t.category} · {t.url}</p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600" data-testid={`stack-clicks-${t.id}`}><MousePointerClick className="h-3.5 w-3.5" /> {t.click_count || 0}</span>
                  <Button size="sm" variant="outline" className="h-8 rounded-md text-xs" onClick={() => setEditing(t.id)} data-testid={`stack-admin-edit-${t.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={async () => { await api.delete(`/stack/admin/tools/${t.id}`); toast.success("Removed"); invalidate(); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="rounded-xl border border-emerald/30 bg-emerald/5 p-5">
          <p className="mb-3 text-sm font-bold text-navy">New tool</p>
          <ToolForm initial={EMPTY} saving={saving} testid="stack-add" onSave={(p) => save(null, p)} />
        </div>
      ) : (
        <Button onClick={() => setAdding(true)} variant="outline" className="rounded-md" data-testid="stack-admin-add"><Plus className="mr-1.5 h-4 w-4" /> Add tool</Button>
      )}
    </div>
  );
}
